import { useState } from "react";
import { useCountyListMutation } from "../../../features/county-list/countyListApiSlice";
import { useQuickPricerMutation } from "../../../features/quick-pricer/quickPricerApiSlice";
import { useQuickPricerPopulateFieldsMutation } from "../../../features/quickpricer-populate-fields/quickPricerPopulateFieldsApiSlice";
import { useGetEligibleProductDetailsMutation } from "../../../features/get-eligible-product-details/getEligibleProductDetailsApiSlice";
import { useGetIneligibleProductsMutation } from "../../../features/get-ineligible-products/getIneligibleProductsApiSlice";
import { usePublicQuickPricerMutation } from "../../../features/quick-pricer-public/publicQuickPricerApiSlice";
import { useGetPublicIneligibleProductsMutation } from "../../../features/get-ineligible-products-public/getPublicIneligibleProductsApiSlice";
import { useGetPublicEligibleProductDetailsMutation } from "../../../features/get-eligible-product-details-public/getPublicEligibleProductDetailsApiSlice";
import {
	selectUserDetails,
	selectOrganizationByInternalUser,
} from "../../../features/auth/authSlice";
import { useSelector } from "react-redux";
import { useErrorMessages } from "../../../utils/error-messages.constants";
export const useQuickPricerApis = (currentUser) => {
	const { getErrorMessage } = useErrorMessages();

	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);
	const [commonErrorMessage, setCommonErrorMessage] = useState("");
	const [commonErrorCode, setCommonErrorCode] = useState("");
	const [commonErrorTitle, setCommonErrorTitle] = useState("");

	const [entityID, setEntityID] = useState("");
	const [userID, setUserID] = useState("");
	const [searchId, setSearchId] = useState("");
	const [products, setProducts] = useState([]);
	const [ineligibleProducts, setIneligibleProducts] = useState([]);
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganizationByInternalUser = useSelector(selectOrganizationByInternalUser);
	const [countyList, { isLoading: ZipCode }] = useCountyListMutation();
	const [getQuickPricer, { isLoading: isQuickPricer }] =
		useQuickPricerMutation();
	const [populateFields, { isLoading: isPopulatingFields }] =
		useQuickPricerPopulateFieldsMutation();
	const [getEligibleProductDetails, { isLoading: isGettingEligible }] =
		useGetEligibleProductDetailsMutation();
	const [getIneligibleProducts, { isLoading: isGettingIneligible }] =
		useGetIneligibleProductsMutation();
	const [getPublicQuickPricer, { isLoading: isPublicQuickPricer }] =
		usePublicQuickPricerMutation();
	const [getPublicIneligibleProducts, { isLoading: isGettingPublicIneligible }] =
		useGetPublicIneligibleProductsMutation();
	const [getPublicEligibleProductDetails, { isLoading: isGettingPublicEligible }] =
		useGetPublicEligibleProductDetailsMutation();

	// Standalone Quick Pricer has no logged-in user -- entity/user IDs
	// are resolved server-side from backend config in that case, so both
	// existing resolution branches below are skipped entirely.
	const isPublicMode = !userDetails;

	// ----------------- Error Handling -----------------
	const handleOpenCommonErrorModal = (message, errorCode, title) => {
		setCommonErrorMessage(message);
		setCommonErrorCode(errorCode || "");
		setCommonErrorTitle(title || "");
		setCommonErrorModalVisible(true);
	};

	const handleCloseCommonErrorModal = () => {
		setCommonErrorModalVisible(false);
	};

	const extractErrorMessage = (err) => {
		if (!err) return "Unknown error occurred";
		return (
			err?.data?.message ||
			err?.error ||
			err?.message ||
			"Unexpected error occurred"
		);
	};

	const extractApiError = (err) => {
		const errorData = getErrorMessage("QUICK_PRICER_ERROR");
		const traceId = err?.data?.data?.traceId || "";
		const statusCode = errorData.code || err?.data?.status_code || err?.status || "";
		const message = traceId
			? `${errorData.message} Trace ID: ${traceId}`
			: errorData.message;
		return { message, statusCode, title: errorData.title };
	};

	// ================================
	//  ADD BACK fetchCounty FUNCTION
	// ================================
	const fetchCounty = async (zipcode, setFormState) => {
		try {
			const response = await countyList({ q: zipcode }).unwrap();
			const locations = response?.data || [];

			if (!locations.length) {
				setFormState((prev) => ({
					...prev,
					city: "",
					county: "",
					state: "",
				}));
				return;
			}

			const location = locations[0];

			setFormState((prev) => ({
				...prev,
				city: location.city || "",
				county: location.county || "",
				state: location.state || "",
			}));
		} catch (error) {
			handleOpenCommonErrorModal(extractErrorMessage(error), error?.data?.errorCode || error?.status || "");
		}
	};

	// ----------------- Quick Pricer Flow -----------------
	const runQuickPricerFlow = async (requestBody) => {
	try {
		if (isPublicMode) {
			let receivedSearchId;

			try {
				const quickPricerResponse = await getPublicQuickPricer({
					ob_loan_data: requestBody,
				}).unwrap();

				receivedSearchId = quickPricerResponse?.data?.searchId;
				const receivedProducts = quickPricerResponse?.data?.products || [];

				setSearchId(receivedSearchId);
				setProducts(receivedProducts);
			} catch (err) {
				console.error("Error in runQuickPricerFlow (public):", err);
				const { message, statusCode, title } = extractApiError(err);
				handleOpenCommonErrorModal(message, statusCode, title);
			}

			if (receivedSearchId) try {
				const ineligibleResponse = await getPublicIneligibleProducts({
					searchId: receivedSearchId,
				}).unwrap();

				setIneligibleProducts(
					ineligibleResponse?.data?.ineligibleProducts || []
				);
			} catch (err) {
				const { message, statusCode, title } = extractApiError(err);
				handleOpenCommonErrorModal(message, statusCode, title);
			}

			return;
		}

		let localEntityID = "";
		let localUserID = "";
		const isExecutiveOrManager =
			userDetails?.user_roles?.includes("admin") ||
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant");
		if (isExecutiveOrManager) {
			const data = userDetails?.snowFlakeOrganizations.find(
				(org) => org.company_name === selectedOrganizationByInternalUser
			);

			localEntityID = data?.ob_business_channel_id;
			localUserID = data?.ob_originator_id;
		} else {
			const populateResponse = await populateFields({
				email: currentUser.email,
				loan_channel: currentUser.loan_channel,
			}).unwrap();

			const rawValue = populateResponse.response.find(
				(item) => item.id === "CX.OB.ENTITY.ID"
			)?.value;

			const numbersPart = rawValue?.match(/\d+\*\d+/)?.[0];
			const [extractedEntityID, extractedUserID] = numbersPart.split("*");

			localEntityID = extractedEntityID;
			localUserID = extractedUserID;
		}

		// OPTIONAL: still store in state if needed elsewhere
		setEntityID(localEntityID);
		setUserID(localUserID);

		console.log("Correct entityID:", localEntityID);
		console.log("Correct userID:", localUserID);

		if (localEntityID && localUserID) {
			let receivedSearchId;

			try {
				const quickPricerResponse = await getQuickPricer({
					ob_loan_data: requestBody,
					ob_business_channel_id: localEntityID,
					ob_originator_id: localUserID,
				}).unwrap();

				receivedSearchId = quickPricerResponse?.data?.searchId;
				const receivedProducts = quickPricerResponse?.data?.products || [];

				setSearchId(receivedSearchId);
				setProducts(receivedProducts);
			} catch (err) {
				console.error("Error in runQuickPricerFlow:", err);
				const { message, statusCode, title } = extractApiError(err);
				handleOpenCommonErrorModal(message, statusCode, title);
			}

			if (receivedSearchId) try {
				const ineligibleResponse = await getIneligibleProducts({
					ob_business_channel_id: localEntityID,
					ob_originator_id: localUserID,
					searchId: receivedSearchId,
				}).unwrap();

				setIneligibleProducts(
					ineligibleResponse?.data?.ineligibleProducts || []
				);
			} catch (err) {
				const { message, statusCode, title } = extractApiError(err);
				handleOpenCommonErrorModal(message, statusCode, title);
			}
		}
	} catch (err) {
		console.error("Error in runQuickPricerFlow:", err);
		const { message, statusCode, title } = extractApiError(err);
		handleOpenCommonErrorModal(message, statusCode, title);
	}
};

	// ----------------- Eligible Product Click -----------------
	const handleProductClick = async (productId) => {
		try {
			const eligibleResponse = isPublicMode
				? await getPublicEligibleProductDetails({
						search_id: searchId,
						product_id: productId,
					}).unwrap()
				: await getEligibleProductDetails({
						ob_business_channel_id: entityID,
						ob_originator_id: userID,
						search_id: searchId,
						product_id: productId,
					}).unwrap();
			console.log("response", productId, eligibleResponse);
			setProducts((prevProducts) =>
				prevProducts.map((product) =>
					product.productId === productId
						? {
								...product,
								productDetails: eligibleResponse,
							}
						: product
				)
			);
		} catch (err) {
			handleOpenCommonErrorModal(extractErrorMessage(err), err?.data?.errorCode || err?.status || "");
		}
	};

	// ----------------- Return -----------------
	return {
		fetchCounty, //  NOW PROPERLY DEFINED
		runQuickPricerFlow,
		handleProductClick,
		products,
		ineligibleProducts,
		loading:
			ZipCode ||
			isPopulatingFields ||
			isQuickPricer ||
			isGettingEligible ||
			isGettingIneligible ||
			isPublicQuickPricer ||
			isGettingPublicIneligible ||
			isGettingPublicEligible,
		commonErrorModalVisible,
		commonErrorMessage,
		commonErrorCode,
		commonErrorTitle,
		handleCloseCommonErrorModal,
	};
};
